import { useCallback, useEffect } from 'react'
import { create } from 'zustand'

import { BusinessVariant, getPricingByIp, PricingByIpResponse } from '@/api/billing'
import { PaymentSchedule, PlanType } from '@/types'
import { getLocaleFromCountryAndCurrency } from '@/utils/locale'

interface PricingState {
  pricing?: PricingByIpResponse
  locale: string
}

const InitialPricingState: PricingState = {
  pricing: undefined,
  locale: navigator.language,
}

interface PricingStoreState {
  pricingState: PricingState
  isInitialized: boolean
  /** The last fetch threw. Distinct from a fetch that succeeded without optional fields. */
  fetchFailed: boolean
  lastFetchTimestamp?: number

  _setPricingState: (pricingState: PricingState) => void
  initialize: () => Promise<void>
}

const CACHE_TTL_MS = 60 * 60 * 1000 // 1 hour in milliseconds

// Dedupes concurrent initialize() calls: with no timestamp after a failure, every
// consumer mount would otherwise fire its own retry against a struggling service.
let inFlightFetch: Promise<void> | null = null

/** A zustand store that fetches pricing data based on IP location. */
const usePricingStore = create<PricingStoreState>((set, get) => ({
  pricingState: InitialPricingState,
  isInitialized: false,
  fetchFailed: false,
  lastFetchTimestamp: undefined,

  /** Sets the pricing state above */
  _setPricingState: (pricingState) => set({ pricingState }),

  /** Initializes the pricing store. */
  initialize: async () => {
    const state = get()

    // Check if cache is still valid
    if (state.isInitialized && state.lastFetchTimestamp) {
      const now = Date.now()
      const cacheAge = now - state.lastFetchTimestamp

      // If cache is still valid (less than 1 hour old), return
      if (cacheAge < CACHE_TTL_MS) {
        return
      }
    }

    if (inFlightFetch) return inFlightFetch

    inFlightFetch = (async () => {
      try {
        // Get pricing data from API based on IP
        const { data } = await getPricingByIp()
        const locale = getLocaleFromCountryAndCurrency(data?.location?.country_code, data?.currency?.toUpperCase())

        set({
          pricingState: { pricing: data, locale },
          isInitialized: true,
          fetchFailed: false,
          lastFetchTimestamp: Date.now(),
        })
      } catch (error) {
        console.error('Failed to initialize pricing store, falling back to USD:', error)
        // Set default USD pricing state on error so the app can still function
        set({
          pricingState: {
            pricing: undefined,
            locale: 'en-US',
          },
          isInitialized: true,
          fetchFailed: true,
          // No timestamp on failure: a failed fetch must not occupy the cache TTL,
          // so the next visit retries instead of serving the failure for an hour.
          lastFetchTimestamp: undefined,
        })
      } finally {
        inFlightFetch = null
      }
    })()
    return inFlightFetch
  },
}))

// Stable selectors to prevent unnecessary re-renders
const selectPricingState = (state: PricingStoreState) => state.pricingState
const selectLocale = (state: PricingStoreState) => state.pricingState.locale
const selectIsInitialized = (state: PricingStoreState) => state.isInitialized
const selectFetchFailed = (state: PricingStoreState) => state.fetchFailed
const selectInitialize = (state: PricingStoreState) => state.initialize

/** Get a reactive handle on pricing state. */
export const usePricing = () => {
  const pricingState = usePricingStore(selectPricingState)
  const locale = usePricingStore(selectLocale)
  const isInitialized = usePricingStore(selectIsInitialized)
  const fetchFailed = usePricingStore(selectFetchFailed)
  const initialize = usePricingStore(selectInitialize)

  // initialize on first mount only
  // initialize is a stable reference from Zustand, safe to include in deps
  useEffect(() => {
    initialize()
  }, [initialize])

  /** Gets the price for a specific plan from the pricing data. Returns undefined if pricing not loaded. */
  const getPriceForPlan = useCallback(
    (planType: PlanType, schedule: PaymentSchedule): number | undefined => {
      if (!pricingState.pricing) {
        return undefined
      }

      // Map plan types to pricing keys
      const pricingKey = (() => {
        // Team plan is now using Pro pricing
        if (planType === PlanType.Pro || planType === PlanType.Team) {
          return schedule === PaymentSchedule.Monthly ? 'FLOW_PRO_MONTHLY' : 'FLOW_PRO_YEARLY'
        }
        if (planType === PlanType.Enterprise) {
          return schedule === PaymentSchedule.Monthly ? 'FLOW_BUSINESS_MONTHLY' : 'FLOW_BUSINESS_YEARLY'
        }
        return undefined
      })()

      if (!pricingKey) {
        return undefined
      }

      const productPricing = pricingState.pricing.pricing[pricingKey]
      if (!productPricing) {
        return undefined
      }

      const { price, currency_factor } = productPricing
      // Price returned from API in lowest denomination for that currency
      return price / currency_factor
    },
    [pricingState.pricing]
  )

  /** Price for a self-serve Business option. Undefined until the backend serves the new pricing fields. */
  const getBusinessOptionPrice = useCallback(
    (variant: BusinessVariant, schedule: PaymentSchedule): number | undefined => {
      const pricing = pricingState.pricing?.pricing
      const option = variant === 'bundle' ? pricing?.business_bundle : pricing?.business_dictation_only
      const cell = schedule === PaymentSchedule.Monthly ? option?.monthly : option?.yearly
      if (!cell) {
        return undefined
      }
      return cell.price / cell.currency_factor
    },
    [pricingState.pricing]
  )

  // Memoize the base currency to avoid recreating formatter unnecessarily
  const currency = pricingState?.pricing?.currency || 'USD'
  const flag = pricingState?.pricing?.flag || null

  /** Format a price with currency symbol using locale-aware formatting. Defaults to USD. */
  const formatPrice = useCallback(
    (price: number, options?: { compact?: boolean }): string => {
      const hasDecimals = price % 1 !== 0
      if (options?.compact) {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          notation: 'compact',
          maximumFractionDigits: 1,
        }).format(price)
      }
      // Create formatter with dynamic fraction digits based on whether price has decimals
      // This is necessary because we can't change fraction digits on a cached formatter
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: hasDecimals ? 2 : 0,
        maximumFractionDigits: hasDecimals ? 2 : 0,
      }).format(price)
    },
    [locale, currency]
  )

  /** Format a number using locale-aware formatting (e.g., thousands separators). */
  const localizeNumber = useCallback(
    (value: number): string => {
      return value.toLocaleString(locale)
    },
    [locale]
  )

  return {
    pricingState,
    isInitialized,
    fetchFailed,
    locale,
    initialize,
    getPriceForPlan,
    getBusinessOptionPrice,
    formatPrice,
    localizeNumber,
    currency,
    flag,
  }
}
